/**
 * useEmployeeDetail — Hook for Employee Deep Dive page
 *
 * Fetches all detail data for a single employee:
 * - User profile
 * - Full wellness log history (with all JSONB fields)
 * - Alert history
 * - Session summaries
 * - Computed trend data (posture, fatigue, wellness over time)
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  fetchUserProfile,
  fetchEmployeeWellnessHistory,
  fetchEmployeeAlertHistory,
  fetchEmployeeSessions, // Use server-side fetching
  type UserRow,
  type WellnessLogRow,
  type WellnessAlertRow,
  type SessionSummary,
} from "../lib/supabase";

export interface EmployeeDetailData {
  profile: UserRow | null;
  latestLog: WellnessLogRow | null;
  logs: WellnessLogRow[];
  alerts: WellnessAlertRow[];
  sessions: SessionSummary[];
  trendData: Array<{
    date: string;
    posture: number;
    fatigue: number;
    wellness: number;
    eyeContact: number;
  }>;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setDays: (days: number) => void;
  days: number;
}

const emotionScoreMap: Record<string, number> = {
  Focused: 90,
  Happy: 95,
  Engaged: 85,
  Neutral: 70,
  Confused: 50,
  Tired: 40,
  Stressed: 35,
  Frustrated: 30,
};

export function useEmployeeDetail(userId: string): EmployeeDetailData {
  const [profile, setProfile] = useState<UserRow | null>(null);
  const [logs, setLogs] = useState<WellnessLogRow[]>([]);
  const [alerts, setAlerts] = useState<WellnessAlertRow[]>([]);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(7);

  const mountedRef = useRef(true);

  const fetchAll = useCallback(async () => {
    if (!userId) return;
    try {
      const [profileData, logsData, alertsData, sessionsData] =
        await Promise.all([
          fetchUserProfile(userId),
          fetchEmployeeWellnessHistory(userId, days),
          fetchEmployeeAlertHistory(userId),
          fetchEmployeeSessions(userId, days),
        ]);

      if (!mountedRef.current) return;

      setProfile(profileData);
      setLogs(logsData);
      setAlerts(alertsData);
      setSessions(sessionsData);
      setError(null);
    } catch (err: any) {
      if (!mountedRef.current) return;
      setError(err.message || "Failed to load employee data");
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [userId, days]);

  useEffect(() => {
    mountedRef.current = true;
    setIsLoading(true);
    fetchAll();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchAll]);

  // Compute derived data
  const latestLog = logs.length > 0 ? logs[0] : null;
  // Sessions are now fetched directly

  // Compute daily trend data
  const trendData = useMemo(() => {
    if (logs.length === 0) return [];

    const dailyMap = new Map<
      string,
      {
        postureSum: number;
        fatigueSum: number;
        wellnessSum: number;
        eyeContactSum: number;
        count: number;
      }
    >();

    for (const log of logs) {
      const dateKey = new Date(log.timestamp).toISOString().split("T")[0];
      const existing = dailyMap.get(dateKey) || {
        postureSum: 0,
        fatigueSum: 0,
        wellnessSum: 0,
        eyeContactSum: 0,
        count: 0,
      };

      const emotionScore = emotionScoreMap[log.emotion_state] || 50;
      const wellness =
        (log.posture_score || 0) * 0.4 +
        (1 - (log.fatigue_index || 0)) * 100 * 0.3 +
        emotionScore * 0.3;

      existing.postureSum += log.posture_score || 0;
      existing.fatigueSum += log.fatigue_index || 0;
      existing.wellnessSum += wellness;
      existing.eyeContactSum += log.attention_metrics?.eye_contact_percent || 0;
      existing.count += 1;
      dailyMap.set(dateKey, existing);
    }

    return Array.from(dailyMap.entries())
      .map(([date, agg]) => ({
        date,
        posture: Math.round(agg.postureSum / agg.count),
        fatigue: Math.round((agg.fatigueSum / agg.count) * 100) / 100,
        wellness: Math.round(agg.wellnessSum / agg.count),
        eyeContact: Math.round(agg.eyeContactSum / agg.count),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [logs]);

  return {
    profile,
    latestLog,
    logs,
    alerts,
    sessions,
    trendData,
    isLoading,
    error,
    refresh: fetchAll,
    setDays,
    days,
  };
}
